// src/pages/GoogleCallback.tsx
// @dx service
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { App, Spin } from 'antd';
import { supabaseClient } from '../../utility/supabaseClient';
import { useCurrentEmployee } from '../../contexts/useCurrentEmployee';
import { consumeOAuthState } from './googleOauth';
import { Sentry } from '../../sentry';

export const GoogleCallback = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { recheckGoogleScopes } = useCurrentEmployee();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      // Consume the CSRF state on every callback outcome — including a no-code
      // error redirect (e.g. ?error=access_denied&state=…) — so a returned
      // state value can never be replayed against a later callback.
      const stateVerified = consumeOAuthState(urlParams.get('state'));

      if (!code) {
        message.error('No authorization code received');
        navigate('/');
        return;
      }

      if (!stateVerified) {
        Sentry.captureMessage('[GoogleCallback] OAuth state mismatch', { level: 'error' });
        message.error('Google authorization could not be verified. Please try connecting again.');
        navigate('/');
        return;
      }

      try {
        const { data: { user } } = await supabaseClient.auth.getUser();

        if (!user) {
          message.error('Please log in first');
          navigate('/login');
          return;
        }

        const exchange = await supabaseClient.functions.invoke<{ success?: boolean; error?: string }>(
          'exchange-google-token',
          { body: { code, redirect_uri: `${window.location.origin}/google-callback` } }
        );
        const data = exchange.data;
        // supabase-js types FunctionsResponse.error as `any`; narrow it ourselves.
        const fnError = exchange.error as { message?: string } | null;

        if (fnError) {
          Sentry.captureMessage('[GoogleCallback] Edge function error:', { level: 'error', extra: { fnError } });
          throw new Error(fnError.message || 'Failed to connect Google account');
        }

        if (!data?.success) {
          Sentry.captureMessage('[GoogleCallback] Token exchange failed:', { level: 'error', extra: { data } });
          throw new Error(data?.error || 'Failed to connect Google account');
        }

        await recheckGoogleScopes();
        message.success('Google account connected successfully');
        const returnUrl = sessionStorage.getItem('googleOAuthReturnUrl');
        sessionStorage.removeItem('googleOAuthReturnUrl');
        navigate(returnUrl || '/');
      } catch (error) {
        Sentry.captureException(error);
        message.error((error as Error).message || 'Failed to connect Google account');
        navigate('/');
      }
    };

    handleCallback();
  }, [navigate, recheckGoogleScopes, message]);

  return <Spin size="large" tip="Connecting Google account..." />;
};
